import { CaptchaFox, RetryError, TimeoutError } from '@captchafox/react';
import { useMeta } from '@interhyp/platform-meta';
import { ErrorMessage } from '@platform/elements';
import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useRef } from 'react';
import { canRememberCaptcha } from "./functions/canRememberCaptcha.js";
import styles from "./InvisibleCaptcha.module.js";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const siteKeys = {
  // Test Key
  dev: 'sk_11111111000000001111111100000000',
  // Real Site Key
  acc: 'sk_UDp2W1W56Dxua2RlBiwNueXVeXsNN',
  // Real Site Key
  prod: 'sk_7ReOl51NoC9IbnjKHP2R4dgFUYAsf'
};
const captchaFoxPrefix = 'CAPTCHA-FOX_';
export const InvisibleCaptcha = /*#__PURE__*/forwardRef(({
  error = '',
  onLoad
}, ref) => {
  const {
    stage
  } = useMeta();
  const captchaTheme = useMemo(() => ({
    general: {
      primary: styles.primaryColor,
      success: styles.successColor,
      error: styles.errorColor,
      borderRadius: 0
    }
  }), []);
  const captchaRef = useRef(null);
  const siteKey = siteKeys[stage ?? 'dev'];
  const executeAndGetCaptchaToken = useCallback(async rememberCaptchaConfig => {
    if (rememberCaptchaConfig && (await canRememberCaptcha(rememberCaptchaConfig.path, rememberCaptchaConfig.method))) {
      // Skip captcha check if remember-captcha cookie is present for this path-method combination (and not expired)
      return `${captchaFoxPrefix}REMEMBER`; // could be also an empty string
    }
    const captchaElement = document.querySelector('#captcha [name="cf-captcha-response"]');
    const existingToken = captchaElement?.value;
    if (existingToken) {
      return existingToken;
    }
    let token;
    try {
      token = await captchaRef.current?.execute();
    } catch (executeError) {
      if (executeError instanceof RetryError || executeError instanceof TimeoutError) {
        // In this case an "empty" token is sent to the backend
        token = 'NO_TOKEN_PROVIDED';
      } else {
        throw new TypeError('No Captcha token provided!');
      }
    }
    return `${captchaFoxPrefix}${token}`;
  }, []);
  useImperativeHandle(ref, () => ({
    executeAndGetCaptchaToken,
    execute: () => captchaRef.current?.execute(),
    resetCaptcha: () => captchaRef.current?.reset(),
    isReady: () => !!captchaRef.current
  }));
  return /*#__PURE__*/_jsxs("div", {
    id: "captcha",
    children: [/*#__PURE__*/_jsx(CaptchaFox, {
      ref: captchaRef,
      sitekey: siteKey,
      onLoad: onLoad,
      mode: "hidden",
      theme: captchaTheme
    }), error && /*#__PURE__*/_jsx(ErrorMessage, {
      children: error
    })]
  });
});